import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import TestimonialsSection from '../components/TestimonialsSection';
import PageMetaLoader from '../components/PageMetaLoader';
import { useI18n } from '../i18n';
import { en, km } from './PortfoliosPage.data';

interface CarouselArrowProps {
  direction: 'left' | 'right';
}

interface CarouselDotProps {
  active: boolean;
}
const PortfoliosPage = () => {
  const { locale, localePath } = useI18n();
  const t = locale === 'km' ? km : en;
  const portfolios = t.portfolios;
  const sectors = t.sectors;

  // State for carousel
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === portfolios.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, [portfolios.length]);

  return (
    <PortfolioContainer>
      <PageMetaLoader pageUrl="portfolios" />

      <SectorsSection>
        <SectionTitle>{t.pageTitle}</SectionTitle>
        <SectorsGrid>
          {sectors.map((sector, index) => (
            <SectorTag key={index}>{sector}</SectorTag>
          ))}
        </SectorsGrid>
      </SectorsSection>

      <ClientsSection>
        <SectionTitle>{t.clientsTitle}</SectionTitle>
        <LogoScrollContainer>
          <LogoScrollMask>
            <LogoScrollTrack>
              {[
                '/images/clients/client-logo-1.png',
                '/images/clients/client-logo-2.png',
                '/images/clients/client-logo-3.png',
                '/images/clients/client-logo-4.png',
                '/images/clients/client-logo-5.jpg',
                '/images/clients/client-logo-6.png',
                '/images/clients/client-logo-7.png',
                // Duplicating to create continuous scroll effect
                '/images/clients/client-logo-1.png',
                '/images/clients/client-logo-2.png',
                '/images/clients/client-logo-3.png',
                '/images/clients/client-logo-4.png',
                '/images/clients/client-logo-5.jpg',
                '/images/clients/client-logo-6.png',
                '/images/clients/client-logo-7.png'
              ].map((logoSrc, index) => (
                <LogoItem key={index}>
                  <LogoImage src={logoSrc} alt={`${t.clientLogoAlt} ${index % 7 + 1}`} />
                </LogoItem>
              ))}
            </LogoScrollTrack>
          </LogoScrollMask>
        </LogoScrollContainer>
      </ClientsSection>


      {/* <PortfolioSection>
        <SectionTitle>Featured Case Studies</SectionTitle>
        <PortfolioGrid>
          {portfolios.map((portfolio, index) => (
            <PortfolioCard key={index}>
              <PortfolioImage>
                <img src={portfolio.image} alt={portfolio.title} />
                <CategoryBadge>{portfolio.category}</CategoryBadge>
              </PortfolioImage>
              <PortfolioContent>
                <h3>{portfolio.title}</h3>
                <p>{portfolio.description}</p>
                <ReadMoreButton to="#">View Case Study</ReadMoreButton>
              </PortfolioContent>
            </PortfolioCard>
          ))}
        </PortfolioGrid>
      </PortfolioSection> */}

      <TestimonialsSection />

      <CTASection>
        <h2>{t.cta.title}</h2>
        <p>{t.cta.body}</p>
        <CTAButton to={localePath('/contact')}>{t.cta.button}</CTAButton>
      </CTASection>
    </PortfolioContainer>
  );
};

const PortfolioContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
`;

const HeroSection = styled.section`
  position: relative;
  padding: 4rem 2rem;
  margin-bottom: 3rem;
  border-radius: 10px;
  background-color: var(--primary-light);
  background-image: url('/images/portfolios/portfolio-banner.jpg');
  background-size: cover;
  background-position: center;
  background-blend-mode: overlay;
  color: white;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(52, 92, 193, 0.7);
    border-radius: 10px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  
  h1 {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    color: white;
  }
  
  p {
    font-size: 1.25rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: var(--primary-color);
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background-color: var(--accent-color);
    margin: 1rem auto 0;
    border-radius: 2px;
  }
`;

const SectorsSection = styled.section`
  margin: 4rem 0;
`;

const SectorsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const SectorTag = styled.div`
  background-color: var(--primary-light);
  color: var(--primary-color);
  padding: 0.5rem 1.5rem;
  border-radius: 30px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const PortfolioSection = styled.section`
  margin: 4rem 0;
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PortfolioCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const PortfolioImage = styled.div`
  position: relative;
  height: 200px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CategoryBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: var(--primary-color);
  color: white;
  padding: 0.3rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const PortfolioContent = styled.div`
  padding: 1.5rem;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
    color: var(--primary-color);
  }
  
  p {
    color: var(--text-dark);
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const ReadMoreButton = styled(Link)`
  color: var(--primary-color);
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  
  &::after {
    content: '→';
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover::after {
    transform: translateX(5px);
  }
`;

const TestimonialSection = styled.section`
  margin: 4rem 0;
`;

const TestimonialCard = styled.div`
  background-color: var(--primary-light);
  padding: 3rem;
  border-radius: 10px;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const TestimonialQuote = styled.blockquote`
  font-size: 1.25rem;
  line-height: 1.8;
  color: var(--text-dark);
  margin-bottom: 2rem;
  font-style: italic;
  
  &::before {
    content: '"';
    font-size: 3rem;
    color: var(--primary-color);
    line-height: 0;
    vertical-align: -0.4em;
    margin-right: 0.25rem;
  }
`;

const TestimonialAuthor = styled.div`
  color: var(--text-dark);
`;

const CTASection = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: 3rem;
  text-align: center;
  border-radius: 10px;
  margin-top: 4rem;
  
  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 2rem;
  background-color: white;
  color: var(--primary-color);
  font-weight: 500;
  border-radius: 40px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const CarouselSection = styled.section`
  margin: 4rem 0;
`;

const CarouselContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 400px;
`;

const CarouselSlider = styled.div`
  display: flex;
  transition: transform 0.5s ease;
`;

const CarouselSlide = styled.div`
  flex: 0 0 100%;
  height: 100%;
  position: relative;
`;

const CarouselImage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const CarouselOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${CarouselImage}:hover & {
    opacity: 1;
  }
  
  h3 {
    font-size: 1.8rem;
    color: white;
    margin-bottom: 0.5rem;
  }
  
  p.category {
    font-size: 1rem;
    color: var(--accent-color);
    margin-bottom: 0.75rem;
    font-weight: 500;
  }
  
  p.description {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
`;

const CarouselButton = styled.button`
  background-color: var(--accent-color);
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 30px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: white;
    color: var(--primary-color);
  }
`;

const CarouselArrow = styled.button<CarouselArrowProps>`
  position: absolute;
  top: 50%;
  ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: var(--primary-color);
  }
`;

const CarouselDots = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
`;

const CarouselDot = styled.button<CarouselDotProps>`
  width: 1rem;
  height: 1rem;
  background-color: ${({ active }) => active ? 'var(--accent-color)' : 'rgba(255, 255, 255, 0.5)'};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  
  &:hover {
    background-color: var(--accent-color);
  }
`;

const ClientsSection = styled.section`
  margin: 4rem 0;
`;

const LogoScrollContainer = styled.div`
  flex: 1;
  padding: 0 5px;
  
  @media (min-width: 768px) {
    padding: 0 6px;
  }
`;

const LogoScrollMask = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  mask: linear-gradient(90deg, transparent, white 5%, white 95%, transparent);
`;

const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const LogoScrollTrack = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  justify-content: center;
  gap: 1rem;
  min-width: max-content;
  animation: ${scrollAnimation} 30s linear infinite;
`;

const LogoItem = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  width: auto;
  height: 5rem;
  transition: all 0.3s;
  padding: 1.5rem 0.625rem;
  margin-right: 1rem;
  background-color: rgba(0, 0, 0, 0.05);
  
  @media (min-width: 768px) {
    height: 6rem;
    padding: 1.5rem 1.25rem;
  }
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const LogoImage = styled.img`
  height: 100%;
  transition: all 0.3s;
  
  &:hover {
    transform: scale(1.05);
  }
`;

export default PortfoliosPage; 